import type { EnvironmentId, PullRequestListResult } from "@cmmd-forge/contracts";
import type { InfiniteData } from "@tanstack/react-query";
import { useEffect } from "react";

import {
  restorePullRequestWarmSnapshot,
  serializePullRequestWarmSnapshot,
} from "../../lib/pullRequestWarmSnapshot";

const WARM_SNAPSHOT_STORAGE_KEY = "forge:pull-requests:warm:v1";

type PageParam = {
  readonly limit: number;
  readonly cursors?: Record<string, string>;
};
type PullRequestWarmSnapshot = NonNullable<ReturnType<typeof restorePullRequestWarmSnapshot>>;

function readSnapshot(environmentIds: ReadonlyArray<EnvironmentId>) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(WARM_SNAPSHOT_STORAGE_KEY);
    return raw === null ? null : restorePullRequestWarmSnapshot(raw, environmentIds);
  } catch {
    return null;
  }
}

function clearSnapshot() {
  try {
    window.localStorage.removeItem(WARM_SNAPSHOT_STORAGE_KEY);
  } catch {
    // Storage is only a stale rendering hint; an unavailable store must not block the inbox.
  }
}

function snapshotEntries(snapshot: PullRequestWarmSnapshot | null, environmentId: EnvironmentId) {
  return snapshot?.entries.filter((entry) => entry.environmentId === environmentId) ?? [];
}

function snapshotInitialData(
  snapshot: PullRequestWarmSnapshot | null,
  environmentId: EnvironmentId,
  pageParam: PageParam,
) {
  const entries = snapshotEntries(snapshot, environmentId);
  if (entries.length === 0) return undefined;
  return {
    pages: [
      {
        viewers: snapshot?.viewersByEnvironment?.[environmentId] ?? {},
        providers: [],
        entries,
        errors: [],
        truncated: false,
        nextCursors: {},
      },
    ],
    pageParams: [pageParam],
  };
}

type PullRequestWarmSnapshotResults = ReadonlyArray<{
  readonly data: InfiniteData<PullRequestListResult, PageParam> | undefined;
  readonly error: Error | null;
  readonly isPending: boolean;
}>;

function hasIncompleteResults(
  environmentIds: ReadonlyArray<EnvironmentId>,
  results: PullRequestWarmSnapshotResults,
): boolean {
  return results.length !== environmentIds.length || results.some(isResultIncomplete);
}

function isResultIncomplete(result: PullRequestWarmSnapshotResults[number]): boolean {
  return result.isPending || result.data === undefined;
}

function hasSnapshotErrors(results: PullRequestWarmSnapshotResults): boolean {
  return results.some((result) => {
    const page = result.data?.pages[0];
    return result.error !== null || (page?.errors.length ?? 0) > 0;
  });
}

function serializeSnapshotResults(
  environmentIds: ReadonlyArray<EnvironmentId>,
  results: PullRequestWarmSnapshotResults,
) {
  const entries = results.flatMap((result, index) => {
    const environmentId = environmentIds[index]!;
    return (result.data?.pages[0]?.entries ?? []).map((entry) =>
      Object.assign({}, entry, { environmentId }),
    );
  });
  const viewersByEnvironment = Object.fromEntries(
    results.map((result, index) => [environmentIds[index]!, result.data?.pages[0]?.viewers ?? {}]),
  );
  return serializePullRequestWarmSnapshot({
    environmentIds,
    entries,
    viewersByEnvironment,
  });
}

function persistPullRequestWarmSnapshot(input: {
  readonly environmentIds: ReadonlyArray<EnvironmentId>;
  readonly enabled: boolean;
  readonly results: PullRequestWarmSnapshotResults;
}) {
  if (!input.enabled) return;
  if (hasIncompleteResults(input.environmentIds, input.results)) return;
  if (hasSnapshotErrors(input.results)) {
    clearSnapshot();
    return;
  }
  writeSnapshot(serializeSnapshotResults(input.environmentIds, input.results));
}

function writeSnapshot(serialized: string): void {
  try {
    window.localStorage.setItem(WARM_SNAPSHOT_STORAGE_KEY, serialized);
  } catch {
    // The live query remains authoritative when storage is unavailable or full.
  }
}

export function usePullRequestWarmSnapshot(input: {
  readonly environmentIds: ReadonlyArray<EnvironmentId>;
  readonly enabled: boolean;
}) {
  const { environmentIds, enabled } = input;
  const snapshot = enabled ? readSnapshot(environmentIds) : null;

  return {
    initialDataFor(environmentId: EnvironmentId, pageParam: PageParam) {
      return snapshotInitialData(snapshot, environmentId, pageParam);
    },
    invalidate: clearSnapshot,
  };
}

export function usePersistPullRequestWarmSnapshot(input: {
  readonly environmentIds: ReadonlyArray<EnvironmentId>;
  readonly enabled: boolean;
  readonly results: PullRequestWarmSnapshotResults;
}) {
  const { environmentIds, enabled, results } = input;
  useEffect(() => {
    persistPullRequestWarmSnapshot({ environmentIds, enabled, results });
  }, [enabled, environmentIds, results]);
}
